'use client'

import { useState, useEffect, Suspense } from 'react'
import { useSearchParams } from 'next/navigation'
import { matches as staticMatches } from '../../lib/scores'
import { API_BASE_URL } from '../../lib/api'
import type { Match } from '../../lib/scores'

const DAY_LABELS: Record<1 | 2, string> = {
  1: 'March 28',
  2: 'April 12',
}

/** Shape returned by /public/schedule */
interface ApiMatch {
  id: string
  week: number
  home_team: string
  away_team: string
  status: string
  home_score: number | null
  away_score: number | null
}

/** Merge API results into the static match list (preserves ordering, kickoff times, game numbers) */
function mergeApiResults(apiMatches: ApiMatch[]): Match[] {
  const byTeams: Record<string, ApiMatch> = {}
  for (const m of apiMatches) {
    byTeams[`${m.home_team}|${m.away_team}`] = m
  }
  return staticMatches.map(m => {
    const api = byTeams[`${m.homeTeam}|${m.awayTeam}`]
    if (api && api.home_score !== null && api.away_score !== null) {
      return {
        ...m,
        homeScore: api.home_score,
        awayScore: api.away_score,
        status: 'final' as const,
      }
    }
    if (api) {
      return { ...m, status: api.status as Match['status'] }
    }
    return m
  })
}

function ScoresContent() {
  const searchParams = useSearchParams()
  const dayParam = searchParams.get('day')
  const [selectedDay, setSelectedDay] = useState<1 | 2>(
    dayParam ? (parseInt(dayParam) as 1 | 2) : 1
  )
  const [allMatches, setAllMatches] = useState<Match[]>(staticMatches)

  useEffect(() => {
    fetch(`${API_BASE_URL}/public/schedule`)
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data: ApiMatch[]) => setAllMatches(mergeApiResults(data)))
      .catch(() => {
        // API unavailable — static schedule (no scores) is fine
      })
  }, [])

  const dayMatches = allMatches.filter(m => m.day === selectedDay)

  return (
    <div>
      <section className="bg-navy text-white">
        <div className="mx-auto max-w-4xl px-6 py-16">
          <p className="text-xs uppercase tracking-[0.4em] text-ice">Results</p>
          <h1 className="mt-4 text-3xl font-semibold">Scores</h1>
          <p className="mt-4 text-white/80">
            Match results for the 2026 Noreaster. Four teams, six matches across two days.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16">
        {/* Day selector */}
        <div className="mb-10 flex justify-center">
          <div className="inline-flex rounded-lg border border-storm/20 bg-white p-1 shadow-sm">
            {([1, 2] as const).map(day => (
              <button
                key={day}
                onClick={() => setSelectedDay(day)}
                className={`rounded-md px-6 py-2 text-sm font-medium transition-colors ${
                  selectedDay === day
                    ? 'bg-navy text-white'
                    : 'text-storm hover:bg-storm/5 hover:text-navy'
                }`}
                aria-label={`View ${DAY_LABELS[day]}`}
              >
                {DAY_LABELS[day]}
              </button>
            ))}
          </div>
        </div>

        {dayMatches.length > 0 ? (
          <div className="mx-auto max-w-2xl">
            <div className="space-y-4">
              {dayMatches.map(match => (
                <MatchCard key={match.id} match={match} />
              ))}
            </div>
            {selectedDay === 2 && (
              <p className="mt-8 text-center text-sm text-storm">Awards & Trophies — 4:15 PM</p>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-storm/20 bg-white p-12 text-center shadow-sm">
            <p className="text-storm">Scores will post after matches conclude.</p>
          </div>
        )}
      </section>
    </div>
  )
}

function MatchCard({ match }: { match: Match }) {
  const isFinal = match.status === 'final'

  return (
    <div className="rounded-lg border border-storm/20 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-storm/10 pb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-storm">Game {match.gameNumber}</span>
            <span className="text-storm">•</span>
            <span className="text-xs text-storm">{match.kickoff}</span>
            <span className="text-storm">•</span>
            <span className="text-xs text-storm">
              {match.venue}
              {match.field ? ` · ${match.field}` : ''}
            </span>
          </div>
          {match.venueAddress && (
            <p className="mt-0.5 text-xs text-storm/60">{match.venueAddress}</p>
          )}
        </div>
        <StatusBadge status={match.status} />
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="font-medium text-navy">{match.homeTeam}</span>
          {isFinal && match.homeScore !== undefined && (
            <span className="text-2xl font-semibold text-navy">{match.homeScore}</span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="font-medium text-navy">{match.awayTeam}</span>
          {isFinal && match.awayScore !== undefined && (
            <span className="text-2xl font-semibold text-navy">{match.awayScore}</span>
          )}
        </div>
      </div>

      {match.note && (
        <div className="mt-4 rounded-md bg-ice/10 px-3 py-2">
          <p className="text-xs text-navy">{match.note}</p>
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: 'final' | 'scheduled' }) {
  if (status === 'final') {
    return (
      <span className="rounded-md bg-navy/10 px-2 py-1 text-xs font-medium uppercase tracking-wider text-navy">
        Final
      </span>
    )
  }

  return (
    <span className="rounded-md bg-storm/10 px-2 py-1 text-xs font-medium uppercase tracking-wider text-storm">
      Scheduled
    </span>
  )
}

export default function ScoresPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ScoresContent />
    </Suspense>
  )
}
